import translations from "@/config/translations";
import useAuthStore from "@/hooks/useAuthStore";
import useDashboardStore from "@/hooks/useDashboardStore";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import StoreSwitcherPopup from "@/UI/popups/StoreSwitcherPopup";
import { useRef } from "react";
import { NavLink } from "react-router-dom";
import "./styles.css";

const NAV_LINKS = [
    { key: "home" as const, path: "/" },
    { key: "branches" as const, path: "/branches" },
    { key: "products" as const, path: "/products" },
    { key: "tiers" as const, path: "/tiers" },
    { key: "challenges" as const, path: "/challenges" },
    { key: "adBanners" as const, path: "/ad-banners" },
    { key: "customers" as const, path: "/customers" },
    { key: "orders" as const, path: "/orders" },
    { key: "feedbacks" as const, path: "/feedbacks" },
    { key: "clients" as const, path: "/clients" },
];

const MODEL_TO_NAV_KEY: Record<string, string> = {
    branches: "branches",
    products: "products",
    tiers: "tiers",
    challenges: "challenges",
    "ad-banners": "adBanners",
    customers: "customers",
    orders: "orders",
    feedbacks: "feedbacks",
    clients: "clients",
    auth: "settings",
};

interface SideBarProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function SideBar({ isOpen, onClose }: SideBarProps) {
    const { lang } = useLangStore();
    const { store } = useDashboardStore();
    const { client } = useAuthStore();
    const { addComp, removeComp } = usePopupStore();
    const t = translations[lang ?? "ar"];
    const badgeRef = useRef<HTMLButtonElement>(null);

    const getStoreName = (): string => {
        if (!store?.name) return "—";
        if (typeof store.name === "string") return store.name;
        if (Array.isArray(store.name)) {
            const match = store.name.find((n: any) => n.lang === lang) ?? store.name[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const storeName = getStoreName();
    const firstLetter = storeName !== "—" ? storeName.charAt(0) : "?";

    const ownedStores: any[] = client?.owner ?? [];
    const permissionStores: any[] = (client?.permissions ?? []).map((p: any) => p.store).filter((s: any) => s && typeof s === "object");
    const allStores = [...ownedStores, ...permissionStores].reduce((acc: any[], s: any) => {
        if (s?._id && !acc.some((x) => x._id === s._id)) acc.push(s);
        return acc;
    }, []);

    const hasMultipleStores = allStores.length > 1;

    const isOwner = ownedStores.some((s: any) => s._id === store?._id);

    const allowedNavKeys: Set<string> | null = isOwner
        ? null
        : new Set([
              "home",
              ...(client?.permissions ?? [])
                  .filter((p: any) => {
                      const storeId = typeof p.store === "object" ? p.store?._id : p.store;
                      return storeId === store?._id;
                  })
                  .map((p: any) => MODEL_TO_NAV_KEY[p.model])
                  .filter(Boolean),
          ]);

    const visibleLinks = NAV_LINKS.filter(({ key }) => !allowedNavKeys || allowedNavKeys.has(key));

    const openSwitcher = () => {
        if (!hasMultipleStores) return;
        const rect = badgeRef.current?.getBoundingClientRect();
        if (!rect) return;
        const name = "store-switcher";
        removeComp(name);
        addComp({
            name,
            Component: StoreSwitcherPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <aside className={`sidebar${isOpen ? " sidebar--open" : ""}`}>
            <button
                ref={badgeRef}
                type="button"
                className={`sidebar-badge${hasMultipleStores ? " sidebar-badge--clickable" : ""}`}
                onClick={openSwitcher}
            >
                <div className="sidebar-logo-wrap">
                    {store?.logo ? (
                        <img className="sidebar-logo" src={store.logo} alt={storeName} />
                    ) : (
                        <span className="sidebar-logo-placeholder">{firstLetter}</span>
                    )}
                </div>
                <div className="sidebar-profile-info">
                    {store?.type && <p className="sidebar-store-type">{store.type}</p>}
                    <p className="sidebar-store-name">{storeName}</p>
                </div>
                {hasMultipleStores && <img src="/icons/chevron-down-icon.svg" className="sidebar-badge-chevron" alt="" />}
            </button>
            <nav className="sidebar-nav">
                {visibleLinks.map(({ key, path }) => (
                    <NavLink
                        key={key}
                        to={path}
                        end={path === "/"}
                        className={({ isActive }) => `sidebar-link${isActive ? " sidebar-link--active" : ""}`}
                        onClick={onClose}
                    >
                        {({ isActive }) => (
                            <>
                                <span className="sidebar-link-label">{t.nav[key]}</span>
                                <img
                                    src={isActive ? "/icons/arrow-dark-icon.svg" : "/icons/arrow-orange-icon.svg"}
                                    className="sidebar-link-arrow"
                                    alt=""
                                />
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}
